import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { CardImage } from "@/components/CardImage";
import { RarityBadge } from "@/components/RarityBadge";
import { CollectionGrid, PortfolioSummary, cardMeta } from "@/components/CollectionBinder";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { MarketPriceTag } from "@/components/MarketPrice";
import { useCardPrice, useCardPrices } from "@/lib/use-card-prices";
import { OFFICIAL_CARD_BY_CODE } from "@/lib/tcg-cards";
import { useSession } from "@/lib/user-data";
import { searchOfficialCards, type OfficialCard } from "@/lib/tcg-cards";
import {
  COLLECTION_CAPACITY,
  COLLECTION_PAGES,
  CONDITIONS,
  CONDITION_LABEL,
  effectiveSell,
  isSold,
  totalsOf,
  usd,
  type CollectionRow,
  type Condition,
} from "@/lib/collection-shared";
import { ShareButton } from "@/components/ShareButton";
import {
  addCollectionCard,
  createCollectionShareLink,
  getMyCollection,
  listCollectionShareLinks,
  moveCollectionCard,
  removeCollectionCard,
  revokeCollectionShareLink,
  updateCollectionCard,
} from "@/lib/collection.functions";

const URL = "https://palstars.com/collection";

export const Route = createFileRoute("/collection")({
  head: () => ({
    meta: [
      { title: "Palworld Card Collection Tracker — Pal Stars" },
      {
        name: "description",
        content:
          "Track every Palworld card you own in a 108-pocket collection binder: buy price, sell price, profit and ROI per card, with eBay asking, sold and graded links, then share your collection with a public link.",
      },
      { property: "og:title", content: "Palworld card collection tracker" },
      { property: "og:description", content: "Log buy price, sell price and profit for every Palworld card you own, then share your binder." },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,follow" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: CollectionPage,
});

interface ShareLink {
  id: string;
  token: string;
  label: string;
  revoked: boolean;
  expires_at: string;
  views: number;
}

function CollectionPage() {
  const { session, loading } = useSession();
  const listFn = useServerFn(getMyCollection);
  const addFn = useServerFn(addCollectionCard);
  const updateFn = useServerFn(updateCollectionCard);
  const removeFn = useServerFn(removeCollectionCard);
  const moveFn = useServerFn(moveCollectionCard);
  const mintFn = useServerFn(createCollectionShareLink);
  const linksFn = useServerFn(listCollectionShareLinks);
  const revokeFn = useServerFn(revokeCollectionShareLink);

  const [rows, setRows] = useState<CollectionRow[]>([]);
  const [links, setLinks] = useState<ShareLink[]>([]);
  const [page, setPage] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<CollectionRow | null>(null);

  // Add-card form state.
  const [slotTarget, setSlotTarget] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<OfficialCard | null>(null);
  const [buy, setBuy] = useState("");
  const [sell, setSell] = useState("");
  const [qty, setQty] = useState("1");
  const [condition, setCondition] = useState<Condition>("raw");
  const [notes, setNotes] = useState("");

  const results = useMemo(() => (query.trim().length < 2 ? [] : searchOfficialCards(query, 12)), [query]);

  const refresh = useCallback(async () => {
    if (!session) return;
    try {
      setRows((await listFn({})) as unknown as CollectionRow[]);
      setLinks((await linksFn({})) as unknown as ShareLink[]);
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not load your collection.");
    }
  }, [session, listFn, linksFn]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const map = useMemo(() => {
    const m = new Map<number, CollectionRow>();
    rows.forEach((r) => {
      if (r.slot !== null) m.set(r.slot, r);
    });
    return m;
  }, [rows]);
  const totals = useMemo(() => totalsOf(rows), [rows]);

  const num = (v: string): number | null => {
    const n = Number(v.replace(/[^0-9.]/g, ""));
    return v.trim() === "" || Number.isNaN(n) ? null : n;
  };

  async function add() {
    if (!picked) {
      setNotice("Pick a card from the Palworld card database first.");
      return;
    }
    try {
      await addFn({
        data: {
          card_code: picked.code,
          quantity: Math.max(1, Number(qty) || 1),
          condition,
          buy_price: num(buy),
          sell_price: num(sell),
          notes,
          slot: slotTarget,
        },
      });
      setPicked(null);
      setQuery("");
      setBuy("");
      setSell("");
      setQty("1");
      setNotes("");
      setSlotTarget(null);
      setNotice(null);
      await refresh();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not add that card.");
    }
  }

  async function saveEdit(patch: {
    buy_price: number | null;
    sell_price: number | null;
    sold_price: number | null;
    sold_at: string | null;
    quantity: number;
    condition: Condition;
    notes: string;
  }) {
    if (!editing) return;
    try {
      await updateFn({ data: { id: editing.id, ...patch } });
      setEditing(null);
      await refresh();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not save that card.");
    }
  }

  /**
   * Marks a pocket as a real sale (or reopens it). The sale price defaults to
   * the asking price the collector already logged, so one click banks the
   * profit; they can refine the exact figure in Edit prices.
   */
  async function toggleSold(row: CollectionRow) {
    try {
      await updateFn({
        data: row.sold_at
          ? { id: row.id, sold_at: null, sold_price: null }
          : { id: row.id, sold_at: new Date().toISOString(), sold_price: row.sold_price ?? row.sell_price ?? null },
      });
      await refresh();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not update that sale.");
    }
  }


  async function remove(row: CollectionRow) {
    try {
      await removeFn({ data: { id: row.id } });
      await refresh();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not remove that card.");
    }
  }

  async function move(id: string, slot: number) {
    try {
      await moveFn({ data: { id, slot } });
      await refresh();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not move that card.");
    }
  }

  async function mint() {
    try {
      const { token } = await mintFn({ data: { label: "Collection share link" } });
      await refresh();
      setNotice(`Share link ready: ${window.location.origin}/collection-share/${token}`);
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not create a share link.");
    }
  }

  // While the session is still resolving (this is also what a crawler and the
  // server-rendered HTML see) the page must still carry its heading and intro
  // copy — a bare spinner made this route look like an empty page in search.
  if (loading || !session) {
    return (
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-14 text-center">
        <h1 className="text-3xl font-bold">Palworld card collection tracker</h1>
        <p className="aeo-answer mt-3 text-sm text-muted-foreground">
          The Pal Stars collection binder tracks every Palworld OFFICIAL CARD GAME card you own with the price you paid,
          the price you sold (or list) it for, and the profit on each pocket. A free account keeps your{" "}
          {COLLECTION_CAPACITY}-pocket binder in sync across devices, and you can share it with a public link that needs
          no account to open.
        </p>
        {loading ? (
          <p className="mt-5 text-sm text-muted-foreground">Loading your collection…</p>
        ) : (
          <Link
            to="/auth"
            className="mt-5 inline-block rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:brightness-110"
          >
            Create a free account
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars · collection tracker</p>
        <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl">Palworld Card Collection Binder</h1>
          <ShareButton title={"Palworld Card Collection Binder — Pal Stars"} text={"My Palworld card collection tracker on Pal Stars."} path="/collection" />
        </div>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          Pick any card from the Pal Stars Palworld card database, drop it into a pocket and log what you paid and what
          you sold it for. Every pocket shows profit per card and deep links into eBay asking, sold and graded searches
          so you can check the current market before you buy or list.
        </p>
      </header>

      {/* Collection → marketplace: sealed product and merch buying live on their
          own board, so send collectors there instead of hunting through /prices. */}
      <nav aria-label="Buying tools" className="flex flex-wrap gap-2 text-xs font-semibold">
        <Link
          to="/marketplace"
          className="rounded-full border border-primary/50 bg-primary/10 px-3 py-1.5 text-primary hover:bg-primary/20"
        >
          Shop sealed packs, boxes &amp; decks on the marketplace →
        </Link>
        <Link to="/prices" className="rounded-full border border-border/60 px-3 py-1.5 hover:border-primary/60">
          Palworld TCG price chart
        </Link>
        <Link
          to="/guides/$slug"
          params={{ slug: "palworld-tcg-deck-building" }}
          className="rounded-full border border-border/60 px-3 py-1.5 hover:border-primary/60"
        >
          Deck building guide
        </Link>
      </nav>

      <AffiliateDisclosure />

      <PortfolioSummary totals={totals} />

      {notice && <p className="glass rounded-xl p-3 text-sm break-all">{notice}</p>}

      <section className="glass rounded-2xl p-5">
        <h2 className="text-xl font-bold">Add a card you own</h2>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          <div>
            <label htmlFor="card-search" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Search the Palworld card database
            </label>
            <input
              id="card-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Lamball, EBP01-001, SSP…"
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            {results.length > 0 && (
              <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto">
                {results.map((c) => (
                  <li key={c.code}>
                    <button
                      type="button"
                      onClick={() => {
                        setPicked(c);
                        setQuery(`${c.name} ${c.code}`);
                      }}
                      className={`flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-sm hover:border-primary/60 ${picked?.code === c.code ? "border-primary bg-primary/10" : "border-border/60"}`}
                    >
                      <CardImage src={c.image} alt="" width={28} height={39} className="rounded" />
                      <span className="flex-1 truncate">
                        {c.name} <span className="text-xs text-muted-foreground">{c.code}</span>
                      </span>
                      <RarityBadge code={c.rarity} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {picked && (
              <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-border/60 bg-card/40 p-2 text-xs">
                <span className="truncate font-semibold">
                  {picked.name} <span className="font-mono text-muted-foreground">{picked.code}</span>
                </span>
                <MarketPriceTag code={picked.code} />
                <MarketFillButton code={picked.code} onUse={(v) => setSell(v.toFixed(2))} />
              </div>
            )}
            <QuickAddRow onPick={(c) => { setPicked(c); setQuery(`${c.name} ${c.code}`); }} />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Buy price (USD)
              <input
                value={buy}
                onChange={(e) => setBuy(e.target.value)}
                inputMode="decimal"
                placeholder="12.50"
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-normal normal-case tracking-normal text-foreground"
              />
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Sell / asking price (USD)
              <input
                value={sell}
                onChange={(e) => setSell(e.target.value)}
                inputMode="decimal"
                placeholder="24.00"
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-normal normal-case tracking-normal text-foreground"
              />
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Copies
              <input
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                inputMode="numeric"
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-normal normal-case tracking-normal text-foreground"
              />
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Condition
              <select
                value={condition}
                onChange={(e) => setCondition(e.target.value as Condition)}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-normal normal-case tracking-normal text-foreground"
              >
                {CONDITIONS.map((c) => (
                  <option key={c} value={c}>
                    {CONDITION_LABEL[c]}
                  </option>
                ))}
              </select>
            </label>
            <label className="sm:col-span-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Notes (where you bought it, slab cert…)
              <input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-normal normal-case tracking-normal text-foreground"
              />
            </label>
            <div className="sm:col-span-2 flex items-center gap-3">
              <button
                type="button"
                onClick={add}
                className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110"
              >
                {slotTarget === null ? "Add to binder" : `Add to pocket #${slotTarget + 1}`}
              </button>
              {slotTarget !== null && (
                <button type="button" onClick={() => setSlotTarget(null)} className="text-xs text-muted-foreground hover:underline">
                  Use first free pocket instead
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="glass rounded-2xl p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-xl font-bold">
            Your binder — {map.size}/{COLLECTION_CAPACITY} pockets
          </h2>
          <p className="text-sm text-muted-foreground">
            Page {page + 1} of {COLLECTION_PAGES}
          </p>
        </div>
        <div className="mt-4">
          <CollectionGrid
            page={page}
            cards={map}
            onEdit={setEditing}
            onRemove={remove}
            onPickSlot={(slot) => {
              setSlotTarget(slot);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            onDropCard={move}
            onToggleSold={toggleSold}

          />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="rounded-md border px-3 py-1.5 text-sm font-semibold hover:bg-card disabled:opacity-40"
          >
            ← Previous page
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(COLLECTION_PAGES - 1, p + 1))}
            disabled={page >= COLLECTION_PAGES - 1}
            className="rounded-md border px-3 py-1.5 text-sm font-semibold hover:bg-card disabled:opacity-40"
          >
            Next page →
          </button>
        </div>
      </section>

      <SalesLedger rows={rows} />


      <section className="glass rounded-2xl p-5">
        <h2 className="text-xl font-bold">Share your collection</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          A share link opens your collection binder for anyone — no Pal Stars account required. Links expire after 30
          days, you can hold up to five active links, and revoking one closes it immediately.
        </p>
        <button
          type="button"
          onClick={mint}
          className="mt-3 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110"
        >
          Create a share link
        </button>
        {links.length > 0 && (
          <ul className="mt-4 space-y-2 text-sm">
            {links.map((l) => (
              <li key={l.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border/60 p-2">
                <code className="flex-1 break-all text-xs">/collection-share/{l.token}</code>
                <span className="text-xs text-muted-foreground">
                  {l.views} views · {l.revoked ? "revoked" : `expires ${new Date(l.expires_at).toISOString().slice(0, 10)}`}
                </span>
                <button
                  type="button"
                  onClick={() => void navigator.clipboard?.writeText(`${window.location.origin}/collection-share/${l.token}`)}
                  className="rounded border px-2 py-1 text-xs hover:border-primary/60"
                >
                  Copy
                </button>
                {!l.revoked && (
                  <button
                    type="button"
                    onClick={async () => {
                      await revokeFn({ data: { id: l.id } });
                      await refresh();
                    }}
                    className="rounded border px-2 py-1 text-xs text-muted-foreground hover:text-destructive"
                  >
                    Revoke
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="glass rounded-2xl p-5">
        <h2 className="text-xl font-bold">Keep going</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li>
            <Link to="/guides/$slug" params={{ slug: "palworld-card-rarity" }} className="font-semibold text-primary hover:underline">
              Palworld card rarity guide →
            </Link>{" "}
            what every rarity code is worth.
          </li>
          <li>
            <Link to="/prices" className="font-semibold text-primary hover:underline">Palworld TCG price chart →</Link>
          </li>
          <li>
            <Link to="/marketplace" className="font-semibold text-primary hover:underline">Palworld TCG marketplace →</Link>
          </li>
          <li>
            <Link to="/cards" className="font-semibold text-primary hover:underline">Palworld card database →</Link>
          </li>
        </ul>
      </section>

      {editing && <EditDialog row={editing} onCancel={() => setEditing(null)} onSave={saveEdit} />}
    </div>
  );
}

function EditDialog({
  row,
  onCancel,
  onSave,
}: {
  row: CollectionRow;
  onCancel: () => void;
  onSave: (patch: {
    buy_price: number | null;
    sell_price: number | null;
    sold_price: number | null;
    sold_at: string | null;
    quantity: number;
    condition: Condition;
    notes: string;
  }) => void;
}) {
  const [buy, setBuy] = useState(row.buy_price === null ? "" : String(row.buy_price));
  const [sell, setSell] = useState(row.sell_price === null ? "" : String(row.sell_price));
  const [soldPrice, setSoldPrice] = useState(row.sold_price === null ? "" : String(row.sold_price));
  const [soldDay, setSoldDay] = useState(row.sold_at ? row.sold_at.slice(0, 10) : "");
  const [qty, setQty] = useState(String(row.quantity));
  const [condition, setCondition] = useState<Condition>((row.condition as Condition) ?? "raw");
  const [notes, setNotes] = useState(row.notes ?? "");
  const num = (v: string): number | null => {
    const n = Number(v.replace(/[^0-9.]/g, ""));
    return v.trim() === "" || Number.isNaN(n) ? null : n;
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4">
      <div role="dialog" aria-label={`Edit ${row.card_code}`} className="glass w-full max-w-md rounded-2xl p-5">
        <h2 className="text-lg font-bold">Edit {row.card_code}</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="text-xs font-semibold text-muted-foreground">
            Buy price
            <input value={buy} onChange={(e) => setBuy(e.target.value)} inputMode="decimal" className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground" />
          </label>
          <label className="text-xs font-semibold text-muted-foreground">
            Asking price
            <input value={sell} onChange={(e) => setSell(e.target.value)} inputMode="decimal" className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground" />
          </label>
          <label className="text-xs font-semibold text-muted-foreground">
            Actually sold for (per copy)
            <input value={soldPrice} onChange={(e) => setSoldPrice(e.target.value)} inputMode="decimal" placeholder="Leave blank if unsold" className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground" />
          </label>
          <label className="text-xs font-semibold text-muted-foreground">
            Sale date
            <input type="date" value={soldDay} onChange={(e) => setSoldDay(e.target.value)} className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground" />
          </label>

          <label className="text-xs font-semibold text-muted-foreground">
            Copies
            <input value={qty} onChange={(e) => setQty(e.target.value)} inputMode="numeric" className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground" />
          </label>
          <label className="text-xs font-semibold text-muted-foreground">
            Condition
            <select value={condition} onChange={(e) => setCondition(e.target.value as Condition)} className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground">
              {CONDITIONS.map((c) => (
                <option key={c} value={c}>{CONDITION_LABEL[c]}</option>
              ))}
            </select>
          </label>
          <label className="sm:col-span-2 text-xs font-semibold text-muted-foreground">
            Notes
            <input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground" />
          </label>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() =>
              onSave({
                buy_price: num(buy),
                sell_price: num(sell),
                sold_price: num(soldPrice),
                sold_at: soldDay
                  ? new Date(`${soldDay}T12:00:00Z`).toISOString()
                  : num(soldPrice) !== null
                    ? new Date().toISOString()
                    : null,
                quantity: Math.max(1, Number(qty) || 1),
                condition,
                notes,
              })

            }
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110"
          >
            Save
          </button>
          <button type="button" onClick={onCancel} className="rounded-md border px-4 py-2 text-sm font-semibold hover:bg-card">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/** One-click "use the live eBay market value as my sell price". */
function MarketFillButton({ code, onUse }: { code: string; onUse: (value: number) => void }) {
  const row = useCardPrice(code);
  if (!row) return null;
  return (
    <button
      type="button"
      onClick={() => onUse(row.market)}
      className="rounded-md border border-primary/50 px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/10"
    >
      Use market price as sell price
    </button>
  );
}

/** Quick-add strip of the most valuable real cards, so a new binder fills fast. */
function QuickAddRow({ onPick }: { onPick: (card: OfficialCard) => void }) {
  const { rows } = useCardPrices();
  const top = rows
    .slice()
    .sort((a, b) => b.market - a.market)
    .map((r) => ({ card: OFFICIAL_CARD_BY_CODE.get(r.code.toUpperCase()), market: r.market }))
    .filter((e): e is { card: OfficialCard; market: number } => Boolean(e.card?.image))
    .slice(0, 8);
  if (top.length === 0) return null;
  return (
    <div className="mt-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Quick add — highest value Palworld cards
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {top.map(({ card, market }) => (
          <button
            key={card.code}
            type="button"
            onClick={() => onPick(card)}
            className="flex items-center gap-1.5 rounded-md border border-border/60 px-1.5 py-1 text-[11px] hover:border-primary/60"
          >
            <CardImage src={card.image} alt="" width={22} height={31} className="rounded" />
            <span className="max-w-[9rem] truncate">{card.name}</span>
            <span className="font-semibold tabular-nums text-emerald-300">${market.toFixed(2)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Real-sales ledger: every pocket the collector has marked sold, newest first,
 * with the cost basis, the price it actually sold for and the banked profit.
 * This is the realized half of the portfolio — the summary above splits banked
 * profit from paper profit using exactly these rows.
 */
function SalesLedger({ rows }: { rows: CollectionRow[] }) {
  const sold = rows
    .filter(isSold)
    .slice()
    .sort((a, b) => String(b.sold_at).localeCompare(String(a.sold_at)));
  if (sold.length === 0) {
    return (
      <section className="glass rounded-2xl p-5">
        <h2 className="text-xl font-bold">Your real sales</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Nothing sold yet. When a card sells, hit <strong className="text-foreground">Mark sold</strong> on its pocket —
          Pal Stars records the sale date and the price it actually went for, then splits your banked profit from the
          paper value of the cards you still hold.
        </p>
      </section>
    );
  }
  const banked = sold.reduce((sum, r) => {
    const sell = effectiveSell(r);
    return sum + (r.buy_price !== null && sell !== null ? (sell - r.buy_price) * Math.max(1, r.quantity) : 0);
  }, 0);
  return (
    <section className="glass rounded-2xl p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xl font-bold">Your real sales</h2>
        <p className="text-sm text-muted-foreground">
          {sold.length} sold ·{" "}
          <strong className={banked >= 0 ? "text-emerald-400" : "text-destructive"}>{usd(banked)}</strong> banked
        </p>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[34rem] text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="py-1 pr-3">Card</th>
              <th className="py-1 pr-3">Sold</th>
              <th className="py-1 pr-3 text-right">Cost</th>
              <th className="py-1 pr-3 text-right">Sale</th>
              <th className="py-1 text-right">Profit</th>
            </tr>
          </thead>
          <tbody>
            {sold.map((r) => {
              const meta = cardMeta(r.card_code);
              const qty = Math.max(1, r.quantity);
              const sell = effectiveSell(r);
              const profit = r.buy_price !== null && sell !== null ? (sell - r.buy_price) * qty : null;
              return (
                <tr key={r.id} className="border-t border-border/40">
                  <td className="py-1.5 pr-3">
                    <span className="font-semibold">{meta.name}</span>{" "}
                    <span className="font-mono text-xs text-muted-foreground">{r.card_code}</span>
                    {qty > 1 ? <span className="text-xs text-muted-foreground"> ×{qty}</span> : null}
                  </td>
                  <td className="py-1.5 pr-3 tabular-nums text-muted-foreground">{String(r.sold_at).slice(0, 10)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{usd(r.buy_price === null ? null : r.buy_price * qty)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{usd(sell === null ? null : sell * qty)}</td>
                  <td className={`py-1.5 text-right font-semibold tabular-nums ${profit === null ? "" : profit >= 0 ? "text-emerald-400" : "text-destructive"}`}>
                    {usd(profit)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
